import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Edit,
  Delete,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Psychology as AIIcon,
  Facebook as FacebookIcon
} from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { currencyFormat } from '../../utils/constants';

// توسعة النوع InventoryItem لدعم الحقول القديمة
interface ExtendedInventoryItem extends Omit<InventoryItem, 'minQuantity'> {
  description?: string;
  price?: number; // سعر البيع القديم
  costPrice?: number;
  minQuantity?: number;
  supplier?: string;
  imageUrl?: string;
  image?: string;
  unitPrice?: number; // إضافة سعر الوحدة
}

interface InventoryItemCardProps {
  item: ExtendedInventoryItem;
  onEdit: () => void;
  onDelete?: () => void;
  onGenerateAI?: () => void;
  onFacebookPost?: () => void;
}

const InventoryItemCard: React.FC<InventoryItemCardProps> = ({
  item,
  onEdit,
  onDelete,
  onGenerateAI,
  onFacebookPost
}) => {
  // عرض تنبيه إذا كانت الكمية أقل من الحد الأدنى
  const isLowStock = item.quantity <= (item.minQuantity || 0);

  // طباعة قيم السعر للتحقق
  console.log('Item in card:', item.name, 'sellingPrice:', item.sellingPrice, 'price:', item.price);

  // تنسيق السعر بالريال السعودي
  const formatPrice = (price: number) => {
    return `${currencyFormat(price)}`;
  };

  // صورة افتراضية في حالة عدم وجود صورة
  const defaultImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAAAAAA7VNdtAAAAAnRSTlMAAQGU/a4AAAAKSURBVHgBY6AMAAAFAAHAADymGQsAAAAASUVORK5CYII='; // Simple grey placeholder

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* صورة العنصر */}
      <CardMedia
        component="img"
        height="140"
        image={item.imageUrl || item.image || defaultImage}
        alt={item.name}
        sx={{ objectFit: 'contain', pt: 1, bgcolor: '#f5f5f5' }}
      />

      {/* تنبيه المخزون المنخفض */}
      {isLowStock && (
        <Chip
          icon={<WarningIcon />}
          label="مخزون منخفض"
          color="warning"
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontSize: '0.75rem'
          }}
        />
      )}

      {/* محتوى البطاقة */}
      <CardContent sx={{ flexGrow: 1, pt: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" noWrap title={item.name}>
            {item.name}
          </Typography>

          <Typography variant="subtitle1" color="primary.main" fontWeight="bold" sx={{
            backgroundColor: '#f0f7ff',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.875rem'
          }}>
            {formatPrice(item.price || 0)}
          </Typography>
        </Box>

        {/* عرض سعر الوحدة إذا كان متوفرًا */}
        {(item.unitPrice !== undefined && item.unitPrice > 0) && (
          <Box display="flex" justifyContent="flex-end" mt={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{
              backgroundColor: '#f5f5f5',
              px: 1,
              py: 0.2,
              borderRadius: 1,
              fontSize: '0.75rem'
            }}>
              سعر الوحدة: {formatPrice(item.unitPrice || 0)}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} noWrap title={item.description}>
          {item.description || 'لا يوجد وصف'}
        </Typography>

        <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
          <Chip
            label={item.category}
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />

          <Box display="flex" alignItems="center">
            <InventoryIcon fontSize="small" color={isLowStock ? 'error' : 'action'} />
            <Typography
              variant="body2"
              color={isLowStock ? 'error' : 'text.secondary'}
              sx={{ ml: 0.5 }}
            >
              {item.quantity} متوفر
            </Typography>
          </Box>
        </Box>

        {/* أزرار التحكم */}
        <Box display="flex" justifyContent="flex-end" mt={1}>
          {onGenerateAI && (
            <Tooltip title="توليد محتوى ذكي">
              <IconButton size="small" onClick={onGenerateAI} color="secondary">
                <AIIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="تعديل">
            <IconButton size="small" onClick={onEdit} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>

          {onDelete && (
            <Tooltip title="حذف">
              <IconButton size="small" onClick={onDelete} color="error">
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {onFacebookPost && (
            <Tooltip title="نشر على فيسبوك">
              <IconButton size="small" onClick={onFacebookPost} sx={{ color: '#1877F2' }}>
                <FacebookIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default InventoryItemCard;